import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface ProfileStepProps {
  data?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
}

const sections = [
  {
    title: 'Personal Information',
    description: 'Your personal details, spouse, and children information'
  },
  {
    title: 'Ministry Information',
    description: 'Church planting details and ministry experience'
  },
  {
    title: 'Media',
    description: 'Sermon reference and photos'
  }
];

const tshirtSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

interface ProfileData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  candidateTshirtSize: string;
  maritalStatus: 'single' | 'married' | 'divorced' | 'widowed' | '';
  
  // Wife Information (matching application form)
  wifeFirstName: string;
  wifeLastName: string;
  wifeEmail: string;
  wifePhone: string;
  wifeTshirtSize: string;
  wifeDateOfBirth: string;
  anniversaryDate: string;
  
  // Children Information
  childrenNamesAges: string;
  
  // Church Planting Information
  targetLocationMississippi: string;
  willBeSouthernBaptist: 'yes' | 'no' | 'unsure' | '';
  
  // Ministry Experience
  ministryLeadershipPositions: string;
  ministryInfluences: string;
  theologicalTraining: string;
  sermonReferenceUrl: string;
  willEmailSermon: boolean;
  
  // Images
  headshotImage: File | null;
  wifeHeadshotImage: File | null;
  familyPhotoImage: File | null;
}

const ProfileStep: React.FC<ProfileStepProps> = ({ data, onComplete, onNext }) => {
  const { user } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
  candidateTshirtSize: '',
    maritalStatus: '',
    wifeFirstName: '',
    wifeLastName: '',
    wifeEmail: '',
    wifePhone: '',
  wifeTshirtSize: '',
    wifeDateOfBirth: '',
    anniversaryDate: '',
    childrenNamesAges: '',
    targetLocationMississippi: '',
    willBeSouthernBaptist: '',
    ministryLeadershipPositions: '',
    ministryInfluences: '',
    theologicalTraining: '',
    sermonReferenceUrl: '',
    willEmailSermon: false,
    headshotImage: null,
    wifeHeadshotImage: null,
    familyPhotoImage: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [existingHeadshotPath, setExistingHeadshotPath] = useState<string | null>(null);
  const [existingWifeHeadshotPath, setExistingWifeHeadshotPath] = useState<string | null>(null);
  const [existingFamilyPhotoPath, setExistingFamilyPhotoPath] = useState<string | null>(null);
  const autoSaveTimer = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (data) {
      // Merge profile data with user registration data (user data takes precedence if profile field is empty)
      setFormData(prevData => {
        const merged = { ...prevData, ...data };
        // Prepopulate from user registration if profile fields are empty
        if (user) {
          if (!merged.firstName && user.firstName) merged.firstName = user.firstName;
          if (!merged.lastName && user.lastName) merged.lastName = user.lastName;
          if (!merged.email && user.email) merged.email = user.email;
        }
        return merged;
      });
      // Track existing image paths
      if ((data as any).headshotImagePath) {
        setExistingHeadshotPath((data as any).headshotImagePath);
      }
      if ((data as any).wifeHeadshotImagePath) {
        setExistingWifeHeadshotPath((data as any).wifeHeadshotImagePath);
      }
      if ((data as any).familyPhotoImagePath) {
        setExistingFamilyPhotoPath((data as any).familyPhotoImagePath);
      }
    } else if (user) {
      // If no profile data exists yet, prepopulate from user registration data
      setFormData(prevData => ({
        ...prevData,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      }));
    }
  }, [data, user]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  // Listen for navigation events from sidebar
  useEffect(() => {
    const handleNavigateToSection = (event: Event) => {
      const customEvent = event as CustomEvent;
      const sectionIndex = customEvent.detail;
      if (sectionIndex >= 0 && sectionIndex < sections.length && sectionIndex !== currentSection) {
        // Clear any pending auto-save and save immediately before navigating
        if (autoSaveTimer.current) {
          clearTimeout(autoSaveTimer.current);
        }
        // Save current section data immediately before navigating away (don't await)
        handleSaveCurrentSection().then(() => {
          setCurrentSection(sectionIndex);
          // Notify parent component to update sidebar highlight
          const updateEvent = new CustomEvent('subsectionChanged', { detail: sectionIndex });
          window.dispatchEvent(updateEvent);
          window.scrollTo(0, 0);
        }).catch(() => {
          // Even if save fails, allow navigation
          setCurrentSection(sectionIndex);
          const updateEvent = new CustomEvent('subsectionChanged', { detail: sectionIndex });
          window.dispatchEvent(updateEvent);
          window.scrollTo(0, 0);
        });
      }
    };

    window.addEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    return () => {
      window.removeEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    };
  }, [currentSection]);

  // Notify parent when section changes internally (via next/prev buttons)
  useEffect(() => {
    const updateEvent = new CustomEvent('subsectionChanged', { detail: currentSection });
    window.dispatchEvent(updateEvent);
  }, [currentSection]);

  // Check if a section is complete
  const isSectionComplete = (sectionIndex: number): boolean => {
    switch (sectionIndex) {
      case 0: // Personal Information
        // Check required personal info fields
        const personalComplete = !!(
          formData.firstName?.trim() &&
          formData.lastName?.trim() &&
          formData.email?.trim() &&
          formData.phone?.trim() &&
          formData.dateOfBirth &&
          formData.candidateTshirtSize?.trim() &&
          formData.maritalStatus
        );
        // If married, wife's first name is required
        if (formData.maritalStatus === 'married' && !formData.wifeFirstName?.trim()) {
          return false;
        }
        if (formData.maritalStatus === 'married' && !formData.wifeDateOfBirth) {
          return false;
        }
        if (formData.maritalStatus === 'married' && !formData.wifeTshirtSize?.trim()) {
          return false;
        }
        if (formData.maritalStatus === 'married' && !formData.anniversaryDate) {
          return false;
        }
        // Validate email format if present
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          return false;
        }
        if (formData.maritalStatus === 'married' && formData.wifeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.wifeEmail)) {
          return false;
        }
        return personalComplete;
      
      case 1: // Ministry Information
        return !!(
          formData.targetLocationMississippi?.trim() &&
          formData.willBeSouthernBaptist &&
          formData.ministryLeadershipPositions?.trim() &&
          formData.ministryInfluences?.trim() &&
          formData.theologicalTraining?.trim()
        );
      
      case 2: // Media
        // Sermon is complete if URL is provided OR willEmailSermon is checked
        const sermonComplete = formData.willEmailSermon || !!(formData.sermonReferenceUrl?.trim());
        // Images are complete if uploaded OR already exist
        const baseImagesComplete = !!(formData.headshotImage || existingHeadshotPath) &&
                               !!(formData.familyPhotoImage || existingFamilyPhotoPath);
        const wifeImagesComplete = formData.maritalStatus === 'married'
          ? !!(formData.wifeHeadshotImage || existingWifeHeadshotPath)
          : true;
        const imagesComplete = baseImagesComplete && wifeImagesComplete;
        return sermonComplete && imagesComplete;
      
      default:
        return false;
    }
  };

  // Calculate completion status for all sections
  const getSectionCompletionStatus = (): boolean[] => {
    return sections.map((_, index) => isSectionComplete(index));
  };

  // Notify parent of completion status whenever formData changes
  useEffect(() => {
    const completionStatus = getSectionCompletionStatus();
    const updateEvent = new CustomEvent('subsectionCompletionChanged', { 
      detail: { completions: completionStatus }
    });
    window.dispatchEvent(updateEvent);
  }, [formData, existingHeadshotPath, existingWifeHeadshotPath, existingFamilyPhotoPath]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (!formData.candidateTshirtSize || !formData.candidateTshirtSize.trim()) {
      newErrors.candidateTshirtSize = 'T-shirt size is required';
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital status is required';
    }

    if (formData.maritalStatus === 'married' && (!formData.wifeFirstName || !formData.wifeFirstName.trim())) {
      newErrors.wifeFirstName = 'Wife\'s first name is required for married applicants';
    }

    if (formData.maritalStatus === 'married' && !formData.wifeDateOfBirth) {
      newErrors.wifeDateOfBirth = 'Wife\'s date of birth is required for married applicants';
    }

    if (formData.maritalStatus === 'married' && (!formData.wifeTshirtSize || !formData.wifeTshirtSize.trim())) {
      newErrors.wifeTshirtSize = 'Wife\'s t-shirt size is required for married applicants';
    }

    if (formData.maritalStatus === 'married' && !formData.anniversaryDate) {
      newErrors.anniversaryDate = 'Anniversary date is required for married applicants';
    }

    if (formData.maritalStatus === 'married' && formData.wifeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.wifeEmail)) {
      newErrors.wifeEmail = 'Please enter a valid wife\'s email address';
    }

    if (!formData.targetLocationMississippi || !formData.targetLocationMississippi.trim()) {
      newErrors.targetLocationMississippi = 'Target location in Mississippi is required';
    }

    if (!formData.willBeSouthernBaptist) {
      newErrors.willBeSouthernBaptist = 'Please specify if this will be a Southern Baptist Church';
    }

    if (!formData.ministryLeadershipPositions || !formData.ministryLeadershipPositions.trim()) {
      newErrors.ministryLeadershipPositions = 'Ministry leadership positions are required';
    }

    if (!formData.ministryInfluences || !formData.ministryInfluences.trim()) {
      newErrors.ministryInfluences = 'Ministry influences are required';
    }

    if (!formData.theologicalTraining || !formData.theologicalTraining.trim()) {
      newErrors.theologicalTraining = 'Theological training information is required';
    }

    // Only require sermon URL if they're not planning to email it
    if (!formData.willEmailSermon) {
      if (!formData.sermonReferenceUrl || !formData.sermonReferenceUrl.trim()) {
        newErrors.sermonReferenceUrl = 'Sermon reference URL is required (or check the box to email it to your hub leader)';
      }
    }

    // Only require images if they haven't been uploaded yet
    if (!formData.headshotImage && !existingHeadshotPath) {
      newErrors.headshotImage = 'Headshot photo is required';
    }

    if (formData.maritalStatus === 'married' && !formData.wifeHeadshotImage && !existingWifeHeadshotPath) {
      newErrors.wifeHeadshotImage = 'Wife\'s headshot photo is required';
    }

    if (!formData.familyPhotoImage && !existingFamilyPhotoPath) {
      newErrors.familyPhotoImage = 'Family photo is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Debounced auto-save for profile data
  const autoSaveDraft = React.useCallback(async (dataToSave: ProfileData) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(async () => {
      try {
        // Build API data object, only including fields with actual values (not empty strings)
        const apiData: any = {};
        
        // Only include fields that have non-empty values
        if (dataToSave.firstName?.trim()) apiData.firstName = dataToSave.firstName.trim();
        if (dataToSave.lastName?.trim()) apiData.lastName = dataToSave.lastName.trim();
        if (dataToSave.email?.trim()) apiData.email = dataToSave.email.trim();
        if (dataToSave.phone?.trim()) apiData.phone = dataToSave.phone.trim();
        if (dataToSave.dateOfBirth) apiData.dateOfBirth = dataToSave.dateOfBirth;
        if (dataToSave.candidateTshirtSize?.trim()) apiData.candidateTshirtSize = dataToSave.candidateTshirtSize.trim();
        if (dataToSave.maritalStatus) apiData.maritalStatus = dataToSave.maritalStatus as 'single' | 'married' | 'divorced' | 'widowed';
        
        // Wife fields - only include if married
        if (dataToSave.maritalStatus === 'married') {
          if (dataToSave.wifeFirstName?.trim()) apiData.wifeFirstName = dataToSave.wifeFirstName.trim();
          if (dataToSave.wifeLastName?.trim()) apiData.wifeLastName = dataToSave.wifeLastName.trim();
          if (dataToSave.wifeEmail?.trim()) apiData.wifeEmail = dataToSave.wifeEmail.trim();
          if (dataToSave.wifePhone?.trim()) apiData.wifePhone = dataToSave.wifePhone.trim();
          if (dataToSave.wifeTshirtSize?.trim()) apiData.wifeTshirtSize = dataToSave.wifeTshirtSize.trim();
          if (dataToSave.wifeDateOfBirth) apiData.wifeDateOfBirth = dataToSave.wifeDateOfBirth;
          if (dataToSave.anniversaryDate) apiData.anniversaryDate = dataToSave.anniversaryDate;
        }
        
        if (dataToSave.childrenNamesAges?.trim()) apiData.childrenNamesAges = dataToSave.childrenNamesAges.trim();
        if (dataToSave.targetLocationMississippi?.trim()) apiData.targetLocationMississippi = dataToSave.targetLocationMississippi.trim();
        if (dataToSave.willBeSouthernBaptist) apiData.willBeSouthernBaptist = dataToSave.willBeSouthernBaptist as 'yes' | 'no' | 'unsure';
        if (dataToSave.ministryLeadershipPositions?.trim()) apiData.ministryLeadershipPositions = dataToSave.ministryLeadershipPositions.trim();
        if (dataToSave.ministryInfluences?.trim()) apiData.ministryInfluences = dataToSave.ministryInfluences.trim();
        if (dataToSave.theologicalTraining?.trim()) apiData.theologicalTraining = dataToSave.theologicalTraining.trim();
        if (dataToSave.sermonReferenceUrl?.trim()) apiData.sermonReferenceUrl = dataToSave.sermonReferenceUrl.trim();

        // Only save if we have at least one field with data
        if (Object.keys(apiData).length > 0) {
          const { portalCandidateService } = await import('../../api/portalCandidateService');
          await portalCandidateService.updateProfile(apiData);
        }
      } catch (error: any) {
        console.error('Error auto-saving profile data:', error);
      }
    }, 1000);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkboxes
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => {
        const updated = { ...prev, [name]: checked };
        // Auto-save draft after a short delay
        autoSaveDraft(updated);
        return updated;
      });
      
      // Clear error when user checks willEmailSermon
      if (name === 'willEmailSermon' && checked && errors.sermonReferenceUrl) {
        setErrors(prev => ({
          ...prev,
          sermonReferenceUrl: '',
        }));
      }
      
      return;
    }
    
    // Auto-add http:// to sermon URL if not present
    if (name === 'sermonReferenceUrl' && value && !value.match(/^https?:\/\//i)) {
      setFormData(prev => {
        const updated = { ...prev, [name]: 'http://' + value };
        // Auto-save draft after a short delay
        autoSaveDraft(updated);
        return updated;
      });
      
      // Clear error
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: '',
        }));
      }
      
      return;
    }
    
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-save draft after a short delay
      autoSaveDraft(updated);
      return updated;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0] || null;
    
    // Validate file size (5MB max for better upload reliability)
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: `File is too large. Maximum size is 5MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB. Please compress the image or choose a smaller file.`,
        }));
        e.target.value = ''; // Clear the file input
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.',
        }));
        e.target.value = ''; // Clear the file input
        return;
      }

      // Auto-upload image when selected
      (async () => {
        try {
          const { portalCandidateService } = await import('../../api/portalCandidateService');
          if (fieldName === 'headshotImage') {
            await portalCandidateService.uploadHeadshot(file);
            setExistingHeadshotPath('uploaded'); // Mark as uploaded
          } else if (fieldName === 'wifeHeadshotImage') {
            await portalCandidateService.uploadWifeHeadshot(file);
            setExistingWifeHeadshotPath('uploaded'); // Mark as uploaded
          } else if (fieldName === 'familyPhotoImage') {
            await portalCandidateService.uploadFamilyPhoto(file);
            setExistingFamilyPhotoPath('uploaded'); // Mark as uploaded
          }
        } catch (error: any) {
          console.error(`Error uploading ${fieldName}:`, error);
          setErrors(prev => ({
            ...prev,
            [fieldName]: error.response?.data?.error || 'Failed to upload image. Please try again.',
          }));
        }
      })();
    }
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: file,
    }));

    // Clear error when user selects a valid file
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Prepare data for API (exclude File objects from JSON payload)
      const apiData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        candidateTshirtSize: formData.candidateTshirtSize,
        maritalStatus: formData.maritalStatus as 'single' | 'married' | 'divorced' | 'widowed' | undefined,
        wifeFirstName: formData.wifeFirstName,
        wifeLastName: formData.wifeLastName,
        wifeEmail: formData.wifeEmail,
        wifePhone: formData.wifePhone,
        wifeTshirtSize: formData.wifeTshirtSize,
        wifeDateOfBirth: formData.wifeDateOfBirth,
        anniversaryDate: formData.anniversaryDate,
        childrenNamesAges: formData.childrenNamesAges,
        targetLocationMississippi: formData.targetLocationMississippi,
        willBeSouthernBaptist: formData.willBeSouthernBaptist as 'yes' | 'no' | 'unsure' | undefined,
        ministryLeadershipPositions: formData.ministryLeadershipPositions,
        ministryInfluences: formData.ministryInfluences,
        theologicalTraining: formData.theologicalTraining,
        sermonReferenceUrl: formData.sermonReferenceUrl,
      };

      // Import the service dynamically to avoid circular imports
      const { portalCandidateService } = await import('../../api/portalCandidateService');
      
      // Save profile data
      await portalCandidateService.updateProfile(apiData);

      // Upload images if they exist
      if (formData.headshotImage) {
        try {
          await portalCandidateService.uploadHeadshot(formData.headshotImage);
        } catch (uploadError: any) {
          if (uploadError.code === 'ECONNABORTED' || uploadError.message?.includes('timeout')) {
            setErrors(prev => ({
              ...prev,
              headshotImage: 'Upload timed out. The image may be too large. Please try compressing it or using a smaller file.',
            }));
            throw new Error('Headshot upload timed out');
          }
          if (uploadError.response?.status === 413) {
            setErrors(prev => ({
              ...prev,
              headshotImage: 'Image file is too large. Please compress the image to under 5MB and try again.',
            }));
            throw new Error('Headshot upload failed: file too large');
          }
          throw uploadError;
        }
      }

      if (formData.wifeHeadshotImage) {
        try {
          await portalCandidateService.uploadWifeHeadshot(formData.wifeHeadshotImage);
        } catch (uploadError: any) {
          if (uploadError.code === 'ECONNABORTED' || uploadError.message?.includes('timeout')) {
            setErrors(prev => ({
              ...prev,
              wifeHeadshotImage: 'Upload timed out. The image may be too large. Please try compressing it or using a smaller file.',
            }));
            throw new Error('Wife headshot upload timed out');
          }
          if (uploadError.response?.status === 413) {
            setErrors(prev => ({
              ...prev,
              wifeHeadshotImage: 'Image file is too large. Please compress the image to under 5MB and try again.',
            }));
            throw new Error('Wife headshot upload failed: file too large');
          }
          throw uploadError;
        }
      }
      
      if (formData.familyPhotoImage) {
        try {
          await portalCandidateService.uploadFamilyPhoto(formData.familyPhotoImage);
        } catch (uploadError: any) {
          if (uploadError.code === 'ECONNABORTED' || uploadError.message?.includes('timeout')) {
            setErrors(prev => ({
              ...prev,
              familyPhotoImage: 'Upload timed out. The image may be too large. Please try compressing it or using a smaller file.',
            }));
            throw new Error('Family photo upload timed out');
          }
          if (uploadError.response?.status === 413) {
            setErrors(prev => ({
              ...prev,
              familyPhotoImage: 'Image file is too large. Please compress the image to under 5MB and try again.',
            }));
            throw new Error('Family photo upload failed: file too large');
          }
          throw uploadError;
        }
      }

      await onComplete(formData);
    } catch (error: any) {
      console.error('Error saving profile data:', error);
      
      // Show user-friendly error message
      if (error.message?.includes('file too large')) {
        // Error already set in specific field above
        return;
      }
      
      setErrors(prev => ({
        ...prev,
        submit: error.response?.data?.error || error.message || 'Failed to save profile. Please try again.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousSection = async () => {
    if (currentSection > 0) {
      // Save current section data before navigating away
      await handleSaveCurrentSection();
      setCurrentSection(currentSection - 1);
    }
  };

  const handleNextSection = async () => {
    if (currentSection < sections.length - 1) {
      // Save current section data before navigating away
      await handleSaveCurrentSection();
      setCurrentSection(currentSection + 1);
    }
  };

  const handleSaveCurrentSection = async () => {
    // Auto-save current section data
    try {
      // Build API data object, only including fields with actual values (not empty strings)
      const apiData: any = {};
      
      // Only include fields that have non-empty values
      if (formData.firstName?.trim()) apiData.firstName = formData.firstName.trim();
      if (formData.lastName?.trim()) apiData.lastName = formData.lastName.trim();
      if (formData.email?.trim()) apiData.email = formData.email.trim();
      if (formData.phone?.trim()) apiData.phone = formData.phone.trim();
      if (formData.dateOfBirth) apiData.dateOfBirth = formData.dateOfBirth;
      if (formData.candidateTshirtSize?.trim()) apiData.candidateTshirtSize = formData.candidateTshirtSize.trim();
      if (formData.maritalStatus) apiData.maritalStatus = formData.maritalStatus as 'single' | 'married' | 'divorced' | 'widowed';
      
      // Wife fields - only include if married
      if (formData.maritalStatus === 'married') {
        if (formData.wifeFirstName?.trim()) apiData.wifeFirstName = formData.wifeFirstName.trim();
        if (formData.wifeLastName?.trim()) apiData.wifeLastName = formData.wifeLastName.trim();
        if (formData.wifeEmail?.trim()) apiData.wifeEmail = formData.wifeEmail.trim();
        if (formData.wifePhone?.trim()) apiData.wifePhone = formData.wifePhone.trim();
        if (formData.wifeTshirtSize?.trim()) apiData.wifeTshirtSize = formData.wifeTshirtSize.trim();
        if (formData.wifeDateOfBirth) apiData.wifeDateOfBirth = formData.wifeDateOfBirth;
        if (formData.anniversaryDate) apiData.anniversaryDate = formData.anniversaryDate;
      }
      
      if (formData.childrenNamesAges?.trim()) apiData.childrenNamesAges = formData.childrenNamesAges.trim();
      if (formData.targetLocationMississippi?.trim()) apiData.targetLocationMississippi = formData.targetLocationMississippi.trim();
      if (formData.willBeSouthernBaptist) apiData.willBeSouthernBaptist = formData.willBeSouthernBaptist as 'yes' | 'no' | 'unsure';
      if (formData.ministryLeadershipPositions?.trim()) apiData.ministryLeadershipPositions = formData.ministryLeadershipPositions.trim();
      if (formData.ministryInfluences?.trim()) apiData.ministryInfluences = formData.ministryInfluences.trim();
      if (formData.theologicalTraining?.trim()) apiData.theologicalTraining = formData.theologicalTraining.trim();
      if (formData.sermonReferenceUrl?.trim()) apiData.sermonReferenceUrl = formData.sermonReferenceUrl.trim();

      // Only save if we have at least one field with data
      const { portalCandidateService } = await import('../../api/portalCandidateService');
      
      if (Object.keys(apiData).length > 0) {
        await portalCandidateService.updateProfile(apiData);
      }

      // Upload images if they exist
      if (formData.headshotImage) {
        await portalCandidateService.uploadHeadshot(formData.headshotImage);
      }
      if (formData.wifeHeadshotImage) {
        await portalCandidateService.uploadWifeHeadshot(formData.wifeHeadshotImage);
      }
      if (formData.familyPhotoImage) {
        await portalCandidateService.uploadFamilyPhoto(formData.familyPhotoImage);
      }
    } catch (error: any) {
      console.error('Error auto-saving profile data:', error);
    }
  };

  const renderSection = () => {
    switch (currentSection) {
      case 0: // Personal Information
        return (
          <div className="space-y-8">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.firstName ? 'border-red-300' : ''
                    }`}
                    placeholder="First name"
                  />
                  {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.lastName ? 'border-red-300' : ''
                    }`}
                    placeholder="Last name"
                  />
                  {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.email ? 'border-red-300' : ''
                    }`}
                    placeholder="Email address"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.phone ? 'border-red-300' : ''
                    }`}
                    placeholder="Phone number"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    id="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.dateOfBirth ? 'border-red-300' : ''
                    }`}
                  />
                  {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
                </div>

                <div>
                  <label htmlFor="candidateTshirtSize" className="block text-sm font-medium text-gray-700">
                    T-shirt Size *
                  </label>
                  <select
                    name="candidateTshirtSize"
                    id="candidateTshirtSize"
                    value={formData.candidateTshirtSize}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.candidateTshirtSize ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select size</option>
                    {tshirtSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  {errors.candidateTshirtSize && <p className="mt-1 text-sm text-red-600">{errors.candidateTshirtSize}</p>}
                </div>

                <div>
                  <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700">
                    Marital Status *
                  </label>
                  <select
                    name="maritalStatus"
                    id="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.maritalStatus ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select marital status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                  {errors.maritalStatus && <p className="mt-1 text-sm text-red-600">{errors.maritalStatus}</p>}
                </div>
              </div>
            </div>

            {/* Wife Information */}
            {formData.maritalStatus === 'married' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Wife's Information</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="wifeFirstName" className="block text-sm font-medium text-gray-700">
                      Wife's First Name *
                    </label>
                    <input
                      type="text"
                      name="wifeFirstName"
                      id="wifeFirstName"
                      value={formData.wifeFirstName}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.wifeFirstName ? 'border-red-300' : ''
                      }`}
                      placeholder="Wife's first name"
                    />
                    {errors.wifeFirstName && <p className="mt-1 text-sm text-red-600">{errors.wifeFirstName}</p>}
                  </div>

                  <div>
                    <label htmlFor="wifeLastName" className="block text-sm font-medium text-gray-700">
                      Wife's Last Name
                    </label>
                    <input
                      type="text"
                      name="wifeLastName"
                      id="wifeLastName"
                      value={formData.wifeLastName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Wife's last name"
                    />
                  </div>

                  <div>
                    <label htmlFor="wifeEmail" className="block text-sm font-medium text-gray-700">
                      Wife's Email
                    </label>
                    <input
                      type="email"
                      name="wifeEmail"
                      id="wifeEmail"
                      value={formData.wifeEmail}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.wifeEmail ? 'border-red-300' : ''
                      }`}
                      placeholder="Wife's email address"
                    />
                    {errors.wifeEmail && <p className="mt-1 text-sm text-red-600">{errors.wifeEmail}</p>}
                  </div>

                  <div>
                    <label htmlFor="wifePhone" className="block text-sm font-medium text-gray-700">
                      Wife's Phone
                    </label>
                    <input
                      type="tel"
                      name="wifePhone"
                      id="wifePhone"
                      value={formData.wifePhone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Wife's phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="wifeTshirtSize" className="block text-sm font-medium text-gray-700">
                      Wife's T-shirt Size *
                    </label>
                    <select
                      name="wifeTshirtSize"
                      id="wifeTshirtSize"
                      value={formData.wifeTshirtSize}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.wifeTshirtSize ? 'border-red-300' : ''
                      }`}
                    >
                      <option value="">Select size</option>
                      {tshirtSizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    {errors.wifeTshirtSize && <p className="mt-1 text-sm text-red-600">{errors.wifeTshirtSize}</p>}
                  </div>

                  <div>
                    <label htmlFor="wifeDateOfBirth" className="block text-sm font-medium text-gray-700">
                      Wife's Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="wifeDateOfBirth"
                      id="wifeDateOfBirth"
                      value={formData.wifeDateOfBirth}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.wifeDateOfBirth ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.wifeDateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.wifeDateOfBirth}</p>}
                  </div>

                  <div>
                    <label htmlFor="anniversaryDate" className="block text-sm font-medium text-gray-700">
                      Anniversary Date *
                    </label>
                    <input
                      type="date"
                      name="anniversaryDate"
                      id="anniversaryDate"
                      value={formData.anniversaryDate}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.anniversaryDate ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.anniversaryDate && <p className="mt-1 text-sm text-red-600">{errors.anniversaryDate}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Children Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Children's Information</h3>
              <div>
                <label htmlFor="childrenNamesAges" className="block text-sm font-medium text-gray-700">
                  Please List Children's Names & Ages
                </label>
                <textarea
                  name="childrenNamesAges"
                  id="childrenNamesAges"
                  rows={3}
                  value={formData.childrenNamesAges}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., John, age 8; Sarah, age 5; or 'No children'"
                />
              </div>
            </div>
          </div>
        );
      
      case 1: // Ministry Information
        return (
          <div className="space-y-8">
            {/* Church Planting Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Church Planting Information</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="targetLocationMississippi" className="block text-sm font-medium text-gray-700">
                    Where in Mississippi do you desire to plant a church? *
                  </label>
                  <input
                    type="text"
                    name="targetLocationMississippi"
                    id="targetLocationMississippi"
                    value={formData.targetLocationMississippi}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.targetLocationMississippi ? 'border-red-300' : ''
                    }`}
                    placeholder="e.g., Jackson, MS or specific area/region"
                  />
                  {errors.targetLocationMississippi && <p className="mt-1 text-sm text-red-600">{errors.targetLocationMississippi}</p>}
                </div>

                <div>
                  <label htmlFor="willBeSouthernBaptist" className="block text-sm font-medium text-gray-700">
                    Will this be a Southern Baptist Church? *
                  </label>
                  <select
                    name="willBeSouthernBaptist"
                    id="willBeSouthernBaptist"
                    value={formData.willBeSouthernBaptist}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.willBeSouthernBaptist ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                  {errors.willBeSouthernBaptist && <p className="mt-1 text-sm text-red-600">{errors.willBeSouthernBaptist}</p>}
                </div>
              </div>
            </div>

            {/* Ministry Experience */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Ministry Experience</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="ministryLeadershipPositions" className="block text-sm font-medium text-gray-700">
                    Please list all past ministry-leadership positions or ministerial employments you have had. (Please include title, church, & duration of each) *
                  </label>
                  <textarea
                    name="ministryLeadershipPositions"
                    id="ministryLeadershipPositions"
                    rows={4}
                    value={formData.ministryLeadershipPositions}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.ministryLeadershipPositions ? 'border-red-300' : ''
                    }`}
                    placeholder="e.g., Youth Pastor, First Baptist Church, 2018-2021..."
                  />
                  {errors.ministryLeadershipPositions && <p className="mt-1 text-sm text-red-600">{errors.ministryLeadershipPositions}</p>}
                </div>

                <div>
                  <label htmlFor="ministryInfluences" className="block text-sm font-medium text-gray-700">
                    Who has influenced you most in the ministry (please list well-known evangelicals as well as more personal influences)? *
                  </label>
                  <textarea
                    name="ministryInfluences"
                    id="ministryInfluences"
                    rows={4}
                    value={formData.ministryInfluences}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.ministryInfluences ? 'border-red-300' : ''
                    }`}
                    placeholder="List people who have influenced your ministry..."
                  />
                  {errors.ministryInfluences && <p className="mt-1 text-sm text-red-600">{errors.ministryInfluences}</p>}
                </div>

                <div>
                  <label htmlFor="theologicalTraining" className="block text-sm font-medium text-gray-700">
                    Where have you been theologically trained? (Please list churches you have been equipped at, colleges, & seminaries) *
                  </label>
                  <textarea
                    name="theologicalTraining"
                    id="theologicalTraining"
                    rows={4}
                    value={formData.theologicalTraining}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.theologicalTraining ? 'border-red-300' : ''
                    }`}
                    placeholder="List your theological training and education..."
                  />
                  {errors.theologicalTraining && <p className="mt-1 text-sm text-red-600">{errors.theologicalTraining}</p>}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 2: // Media
        return (
          <div className="space-y-8">
            {/* Sermon Reference */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sermon Reference</h3>
              <div>
                <label htmlFor="sermonReferenceUrl" className="block text-sm font-medium text-gray-700">
                  SERMON REFERENCE (Provide ONE LINK only to one of your online sermons) {!formData.willEmailSermon && '*'}
                </label>
                <input
                  type="url"
                  name="sermonReferenceUrl"
                  id="sermonReferenceUrl"
                  value={formData.sermonReferenceUrl}
                  onChange={handleInputChange}
                  disabled={formData.willEmailSermon}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    errors.sermonReferenceUrl ? 'border-red-300' : ''
                  } ${formData.willEmailSermon ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="https://example.com/sermon (we'll auto-add http:// if you don't include it)"
                />
                {errors.sermonReferenceUrl && <p className="mt-1 text-sm text-red-600">{errors.sermonReferenceUrl}</p>}
                
                {/* Checkbox for emailing sermon */}
                <div className="mt-3 flex items-start">
                  <input
                    type="checkbox"
                    id="willEmailSermon"
                    name="willEmailSermon"
                    checked={formData.willEmailSermon}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                  />
                  <label htmlFor="willEmailSermon" className="ml-2 block text-sm text-gray-700">
                    I don't have an online sermon link yet - I'll email a sermon recording to my 242 Network hub leader
                  </label>
                </div>
              </div>
            </div>

            {/* Image Uploads */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Photos</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="headshotImage" className="block text-sm font-medium text-gray-700">
                    Headshot Photo {!existingHeadshotPath && '*'}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB. Accepted formats: JPEG, PNG, GIF, WebP</p>
                  {existingHeadshotPath && (
                    <div className="mt-2 mb-2">
                      <p className="text-sm text-green-600 mb-2">✓ Already uploaded</p>
                      <p className="text-xs text-gray-500">Upload a new image to replace the existing one</p>
                    </div>
                  )}
                  <input
                    type="file"
                    name="headshotImage"
                    id="headshotImage"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'headshotImage')}
                    className={`mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 ${
                      errors.headshotImage ? 'border-red-300' : ''
                    }`}
                  />
                  {errors.headshotImage && <p className="mt-1 text-sm text-red-600">{errors.headshotImage}</p>}
                  {formData.headshotImage && (
                    <p className="mt-1 text-sm text-gray-600">
                      Selected: {formData.headshotImage.name} ({(formData.headshotImage.size / 1024 / 1024).toFixed(2)}MB)
                    </p>
                  )}
                </div>

                {formData.maritalStatus === 'married' && (
                  <div>
                    <label htmlFor="wifeHeadshotImage" className="block text-sm font-medium text-gray-700">
                      Wife's Headshot Photo {!existingWifeHeadshotPath && '*'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB. Accepted formats: JPEG, PNG, GIF, WebP</p>
                    {existingWifeHeadshotPath && (
                      <div className="mt-2 mb-2">
                        <p className="text-sm text-green-600 mb-2">✓ Already uploaded</p>
                        <p className="text-xs text-gray-500">Upload a new image to replace the existing one</p>
                      </div>
                    )}
                    <input
                      type="file"
                      name="wifeHeadshotImage"
                      id="wifeHeadshotImage"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'wifeHeadshotImage')}
                      className={`mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 ${
                        errors.wifeHeadshotImage ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.wifeHeadshotImage && <p className="mt-1 text-sm text-red-600">{errors.wifeHeadshotImage}</p>}
                    {formData.wifeHeadshotImage && (
                      <p className="mt-1 text-sm text-gray-600">
                        Selected: {formData.wifeHeadshotImage.name} ({(formData.wifeHeadshotImage.size / 1024 / 1024).toFixed(2)}MB)
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="familyPhotoImage" className="block text-sm font-medium text-gray-700">
                    Family Photo {!existingFamilyPhotoPath && '*'}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB. Accepted formats: JPEG, PNG, GIF, WebP</p>
                  {existingFamilyPhotoPath && (
                    <div className="mt-2 mb-2">
                      <p className="text-sm text-green-600 mb-2">✓ Already uploaded</p>
                      <p className="text-xs text-gray-500">Upload a new image to replace the existing one</p>
                    </div>
                  )}
                  <input
                    type="file"
                    name="familyPhotoImage"
                    id="familyPhotoImage"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'familyPhotoImage')}
                    className={`mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 ${
                      errors.familyPhotoImage ? 'border-red-300' : ''
                    }`}
                  />
                  {errors.familyPhotoImage && <p className="mt-1 text-sm text-red-600">{errors.familyPhotoImage}</p>}
                  {formData.familyPhotoImage && (
                    <p className="mt-1 text-sm text-gray-600">
                      Selected: {formData.familyPhotoImage.name} ({(formData.familyPhotoImage.size / 1024 / 1024).toFixed(2)}MB)
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{sections[currentSection].title}</h2>
        <p className="text-gray-600">
          {sections[currentSection].description}
        </p>
      </div>

      <form onSubmit={async (e) => {
        e.preventDefault();
        await handleSaveCurrentSection();
        if (validateForm()) {
          await handleSubmit(e as any);
        }
      }} className="space-y-8">
        {renderSection()}

        {/* Form Actions */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handlePreviousSection}
            disabled={currentSection === 0}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveCurrentSection}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Section
            </button>
            
            {currentSection < sections.length - 1 ? (
              <button
                type="button"
                onClick={handleNextSection}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Next Section
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save and Continue'
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileStep;
